import { useEffect, useState } from 'react';
import ImportLink from '../components/import/ImportLink';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import PhotoSection from '../components/PhotoSection';
import RecordAutocomplete from '../components/RecordAutocomplete';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import QRCodeLabel from '../components/QRCodeLabel';
import CreatedCodes from '../components/CreatedCodes';
import SlantStoragePanel from '../components/SlantStoragePanel';
import { describeLocation, shortLocation } from '../components/specimens/StoragePanel';
import { SLANT_STATUS_COLORS as STATUS_COLORS } from '../constants/statusColors';
import FieldError, { InvalidWithin, errorIdFor, invalidBorder, invalidProps } from '../components/FieldError';
import { collectErrors, hasErrors, required, useFieldErrors, validationMessage } from '../utils/formValidation';
import OrganicEmptyState from '../components/organic/OrganicEmptyState.jsx';
import OrganicLoader from '../components/organic/OrganicLoader.jsx';
import useFocusOnOpen from '../utils/useFocusOnOpen';
import MediumOptions from '../components/culture/MediumOptions';

// routes/slants.py ISOLATE_CODE_RE: the same rule the server applies.
const ISOLATE_CODE_RE = /^[A-Za-z0-9][A-Za-z0-9._-]{1,47}$/;

const ROLE_COLORS = {
  active:  { background: 'var(--c-info-soft)', color: 'var(--c-info-fg)' },
  refresh: { background: 'var(--c-warning-soft)', color: 'var(--c-warning-text-fg)' },
  backup:  { background: 'var(--c-purple-soft-3)', color: 'var(--c-purple-2-fg)' },
};

const PURPOSES = ['research_use', 'refresh', 'viability_check'];

const badge = (text, colorMap, translateFn, t) => {
  const style = colorMap[text] || { background: 'var(--c-bg-muted)', color: 'var(--c-text-body-fg)' };
  return (
    <span style={{
      ...style,
      padding: '2px 10px',
      borderRadius: 12,
      fontSize: 12,
      fontWeight: 600,
      whiteSpace: 'nowrap',
    }}>
      {text ? t(`${translateFn}.${text}`) : ''}
    </span>
  );
};

const EMPTY_USAGE = { used_by: '', purpose: 'research_use', notes: '' };
const EMPTY_REVIEW = { reviewed_by: '', status_at_review: 'healthy', action_taken: '', notes: '' };

export default function Slants() {
  const { t } = useTranslation(['slants', 'common']);
  // The storage units' names are the specimens' (one list, utils.STORAGE_UNITS).
  const { t: tSpec } = useTranslation('specimens');
  const { user } = useAuth();
  // internal_expert is intentionally excluded — spec: "No edit access to lab data (view only)"
  const canDelete = user?.roles?.some(r => ['collection_manager', 'lab_manager'].includes(r));
  const canAssign = user?.roles?.some(r => ['collection_manager', 'lab_manager'].includes(r));
  // POST /slants/primary: the lab writers (slants.py ELEVATED_ROLES). Cutting a
  // set is wider ("slant:cut": researchers and system admins too).
  const canBankPrimary = user?.roles?.some(r => ['collection_manager', 'lab_technician', 'lab_manager'].includes(r));
  // PUT /slants/{id}/storage: the same roles (slants.py ELEVATED_ROLES).
  const canStore = canBankPrimary;
  const location = useLocation();
  const navigate = useNavigate();
  const [detailSlant, setDetailSlant] = useState(null);
  const [slantAssignments, setSlantAssignments]     = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [allUsers, setAllUsers]                     = useState([]);
  const [assignUserId, setAssignUserId]             = useState('');
  const [assignError, setAssignError]               = useState('');
  const [assignSaving, setAssignSaving]             = useState(false);
  const [slants, setSlants]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [showForm, setShowForm]     = useState(false);
  const [tableExpanded, setTableExpanded] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Codes just created, shown as printable QR labels, and what was made.
  const [createdCodes, setCreatedCodes] = useState([]);
  const [createdMsg, setCreatedMsg] = useState('');
  // A new line with no plate or specimen on record (POST /slants/primary).
  // `unidentified` is the explicit "species not known yet": a line may be
  // banked before it is identified, but not by leaving the species blank.
  const EMPTY_PRIMARY = { open: false, isolate_code: '', species: '', unidentified: false, protocol: '', notes: '', saving: false, error: '' };
  const [primary, setPrimary] = useState(EMPTY_PRIMARY);
  const [primaryProtocols, setPrimaryProtocols] = useState([]);
  const [formError, setFormError]   = useState('');
  // Field-level errors: the slant-set form's plate, the primary isolate's code.
  const setErrs = useFieldErrors();
  const primaryErrs = useFieldErrors();
  // Each form takes focus as it opens: its opener may be the empty state,
  // which goes away when a form is open.
  const setFormHeadingRef = useFocusOnOpen(showForm);
  const primaryHeadingRef = useFocusOnOpen(primary.open);

  // per-row modals: { slantId, type: 'usage'|'review' }
  const [activeModal, setActiveModal] = useState(null);
  const [modalForm, setModalForm]     = useState({});
  const [modalError, setModalError]   = useState('');
  const [modalSaving, setModalSaving] = useState(false);
  const [userList, setUserList]       = useState([]);
  const [photoSlant, setPhotoSlant]   = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingSlant, setDeletingSlant] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [selectedPlate, setSelectedPlate]     = useState(null);
  const [collectionsList, setCollectionsList] = useState([]);
  const [externalList, setExternalList]       = useState([]);

  const load = () => {
    setLoading(true);
    api.get('/slants')
      .then(res => setSlants(res.data))
      .catch(err => setError(err.response?.data?.error || t('failedToLoad')))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api.get('/users/names').then(res => setUserList(res.data)).catch(() => {});
    api.get('/collections').then(res => setCollectionsList(res.data)).catch(() => {});
    api.get('/external').then(res => setExternalList(res.data)).catch(() => {});
  }, []);

  useEffect(() => { setTableExpanded(!showForm); }, [showForm]);

  // Arrived via a QR-code lookup — open that slant's read-only detail directly
  // once the list has loaded, then clear the nav state.
  useEffect(() => {
    if (location.state?.openSlantId && slants.length > 0) {
      const match = slants.find(s => s.slant_id === location.state.openSlantId);
      if (match) setDetailSlant(match);
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, slants]);

  useEffect(() => {
    if (!detailSlant) return;
    setSlantAssignments([]);
    setAssignUserId('');
    setAssignError('');
    if (canAssign) {
      fetchSlantAssignments(detailSlant.slant_id);
      if (allUsers.length === 0) {
        api.get('/users/names').then(res => setAllUsers(res.data)).catch(() => {});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailSlant]);

  const fetchSlantAssignments = (slantId) => {
    setAssignmentsLoading(true);
    api.get(`/slants/${slantId}/assignments`)
      .then(res => setSlantAssignments(res.data))
      .catch(() => {})
      .finally(() => setAssignmentsLoading(false));
  };

  const submitSlantAssignment = () => {
    if (!assignUserId || !detailSlant) return;
    setAssignSaving(true);
    setAssignError('');
    api.post(`/slants/${detailSlant.slant_id}/assignments`, { user_id: assignUserId })
      .then(() => { setAssignUserId(''); fetchSlantAssignments(detailSlant.slant_id); })
      .catch(err => setAssignError(err.response?.data?.error || t('common:saveFailed')))
      .finally(() => setAssignSaving(false));
  };

  const removeSlantAssignment = (assignmentId) => {
    api.delete(`/lab-assignments/${assignmentId}`)
      .then(() => setSlantAssignments(l => l.filter(a => a.assignment_id !== assignmentId)))
      .catch(() => {});
  };

  const confirmDeleteSlant = () => {
    setDeletingSlant(true);
    setDeleteError('');
    api.delete(`/slants/${deleteTarget}`)
      .then(() => { setDeleteTarget(null); load(); })
      .catch(err => setDeleteError(err.response?.data?.error || t('actions.deleteFailed')))
      .finally(() => setDeletingSlant(false));
  };

  // PUT /slants/{id}/storage answers with the slant's whole storage block:
  // laid over the open detail and its row, without reloading the bank.
  const storageSaved = (block) => {
    setDetailSlant(d => (d && d.slant_id === block.slant_id ? { ...d, ...block } : d));
    setSlants(list => list.map(s => (s.slant_id === block.slant_id ? { ...s, ...block } : s)));
  };

  const plateHasSlants = !!(selectedPlate && slants.some(s => s.plate_id === selectedPlate.plate_id));

  const handleSubmit = (e) => {
    e.preventDefault();
    // POST /slants needs the plate the set is cut from; said beside the picker.
    const errors = collectErrors([['plate', required(selectedPlate?.plate_id, 'form.selectPlateError')]]);
    if (hasErrors(errors)) {
      setFormError('');
      setErrs.show(errors);
      return;
    }
    setErrs.reset();
    if (plateHasSlants) {
      setFormError(t('form.alreadyExistsError'));
      return;
    }
    setSubmitting(true);
    setFormError('');
    api.post('/slants', { plate_id: selectedPlate.plate_id })
      .then((res) => {
        const codes = (res.data?.slants || []).map(sl => sl.slant_code);
        setCreatedCodes(codes);
        setCreatedMsg(t('form.createdMsg', { count: codes.length, plate: selectedPlate.plate_code }));
        setShowForm(false); setSelectedPlate(null); load();
      })
      .catch(err => setFormError(err.response?.data?.error || t('form.createFailed')))
      .finally(() => setSubmitting(false));
  };

  const openPrimary = () => {
    setShowForm(false);
    primaryErrs.reset();
    setPrimary({ ...EMPTY_PRIMARY, open: true });
    api.get('/protocols').then(res => setPrimaryProtocols(Array.isArray(res.data) ? res.data : [])).catch(() => {});
  };

  const submitPrimary = (e) => {
    e.preventDefault();
    const code = primary.isolate_code.trim();
    const species = primary.unidentified ? '' : primary.species.trim();
    // The code is optional (the server makes one up), but when given it is a
    // label on a tube and must be one the server will take. A species is
    // needed unless the line is explicitly marked as not identified yet: an
    // empty form must not bank a strain of nothing.
    const errors = collectErrors([
      ['isolate_code', code && !ISOLATE_CODE_RE.test(code)
        ? { code: 'invalid', key: 'primary.codeInvalid' } : null],
      ['species', primary.unidentified ? null : required(species, 'primary.speciesRequired')],
    ]);
    if (hasErrors(errors)) {
      setPrimary(p => ({ ...p, error: '' }));
      primaryErrs.show(errors);
      return;
    }
    primaryErrs.reset();
    setPrimary(p => ({ ...p, saving: true, error: '' }));
    api.post('/slants/primary', {
      ...(code ? { isolate_code: code } : {}),
      ...(species ? { species } : {}),
      ...(primary.protocol ? { protocol: primary.protocol } : {}),
      ...(primary.notes.trim() ? { notes: primary.notes.trim() } : {}),
    })
      .then((res) => {
        const slantCodes = (res.data.slants || []).map(sl => sl.slant_code);
        setCreatedCodes([res.data.isolate_code, ...slantCodes]);
        setCreatedMsg(t('primary.createdMsg', { code: res.data.isolate_code, count: slantCodes.length }));
        setPrimary(EMPTY_PRIMARY);
        load();
      })
      .catch(err => setPrimary(p => ({ ...p, saving: false,
        error: err.response?.data?.error || t('form.createFailed') })));
  };

  const openModal = (slantId, type) => {
    setActiveModal({ slantId, type });
    setModalForm(type === 'usage'
      ? { ...EMPTY_USAGE, used_by: user?.user_id || '' }
      : { ...EMPTY_REVIEW, reviewed_by: user?.user_id || '' }
    );
    setModalError('');
  };

  const closeModal = () => { setActiveModal(null); setModalForm({}); setModalError(''); };

  const handleModalChange = (e) => {
    const { name, value } = e.target;
    setModalForm(f => ({ ...f, [name]: value }));
  };

  const submitModal = (e) => {
    e.preventDefault();
    const { slantId, type } = activeModal;
    const endpoint = type === 'usage'
      ? `/slants/${slantId}/usage`
      : `/slants/${slantId}/reviews`;
    setModalSaving(true);
    setModalError('');
    api.post(endpoint, modalForm)
      .then(() => { closeModal(); load(); })
      .catch(err => setModalError(err.response?.data?.error || t('saveFailed')))
      .finally(() => setModalSaving(false));
  };

  // The Create Slant Set button's handler; the empty bank offers the same thing.
  const toggleSlantForm = () => {
    setShowForm(s => !s); setFormError(''); setErrs.reset(); setSelectedPlate(null); setPrimary(EMPTY_PRIMARY);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>{t('pageTitle')}</h2>
        <button onClick={toggleSlantForm}
                style={btnStyle('var(--c-primary)')} title={t('tip.createSlantSet')}>
          {showForm ? t('common:cancel') : t('createSlantSetBtn')}
        </button>
        {canBankPrimary && (
          <button onClick={() => (primary.open ? setPrimary(EMPTY_PRIMARY) : openPrimary())}
                  style={btnStyle('var(--c-info)')} data-testid="primary-open" title={t('tip.primary')}>
            {primary.open ? t('common:cancel') : t('primary.openBtn')}
          </button>
        )}
        <ImportLink kind="slants" />
      </div>

      {createdMsg && <p role="status" data-testid="slants-created" style={{ color: 'var(--c-success-2-fg)' }}>✓ {createdMsg}</p>}
      <CreatedCodes codes={createdCodes} onDismiss={() => { setCreatedCodes([]); setCreatedMsg(''); }} />

      {primary.open && (
        <form onSubmit={submitPrimary} style={formStyle} data-testid="primary-form" ref={primaryErrs.containerRef}>
          <h3 ref={primaryHeadingRef} tabIndex={-1} style={{ marginTop: 0 }} data-testid="primary-title">{t('primary.title')}</h3>
          <p style={{ marginTop: 0, fontSize: 13, color: 'var(--c-text-muted-fg)' }}>{t('primary.hint')}</p>
          <p style={requiredNoteStyle}>{t('common:validation.requiredNote')}</p>
          {primary.error && <p role="alert" style={formErrorStyle}>{primary.error}</p>}
          <div style={gridStyle}>
            <div style={labelStyle} data-field="isolate_code">
              <label htmlFor="primary-code">{t('primary.code')}</label>
              <input id="primary-code" value={primary.isolate_code} dir="ltr" data-testid="primary-code"
                     {...invalidProps(errorIdFor('primary-code'), !!primaryErrs.errors.isolate_code)}
                     style={{ ...inputStyle, ...invalidBorder(!!primaryErrs.errors.isolate_code) }}
                     placeholder={t('primary.codePlaceholder')} title={t('primary.codeTip')}
                     onChange={e => { setPrimary(p => ({ ...p, isolate_code: e.target.value })); primaryErrs.clear('isolate_code'); }} />
              <FieldError id={errorIdFor('primary-code')} testId="primary-code-error">
                {validationMessage(t, primaryErrs.errors.isolate_code)}
              </FieldError>
            </div>
            <div style={labelStyle} data-field="species">
              <label htmlFor="primary-species">{t('primary.species')}</label>
              <input id="primary-species" value={primary.species} dir="ltr" data-testid="primary-species" title={t('primary.speciesTip')}
                     disabled={primary.unidentified} aria-required={primary.unidentified ? undefined : 'true'}
                     {...invalidProps(errorIdFor('primary-species'), !!primaryErrs.errors.species)}
                     style={{ ...inputStyle, ...(primary.unidentified ? { background: 'var(--c-bg-muted)' } : null), ...invalidBorder(!!primaryErrs.errors.species) }}
                     onChange={e => { setPrimary(p => ({ ...p, species: e.target.value })); primaryErrs.clear('species'); }} />
              <FieldError id={errorIdFor('primary-species')} testId="primary-species-error">
                {validationMessage(t, primaryErrs.errors.species)}
              </FieldError>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 400 }}>
                <input type="checkbox" checked={primary.unidentified} data-testid="primary-unidentified"
                       title={t('primary.unidentifiedTip')}
                       onChange={e => { const on = e.target.checked; setPrimary(p => ({ ...p, unidentified: on })); primaryErrs.clear('species'); }} />
                {t('primary.unidentified')}
              </label>
            </div>
            <label style={labelStyle}>{t('primary.protocol')}
              <select value={primary.protocol} style={inputStyle} title={t('primary.protocolTip')}
                      onChange={e => setPrimary(p => ({ ...p, protocol: e.target.value }))}>
                <option value="">{t('primary.noProtocol')}</option>
                <MediumOptions protocols={primaryProtocols} />
              </select>
            </label>
            <label style={{ ...labelStyle, gridColumn: 'span 2' }}>{t('primary.notes')}
              <textarea value={primary.notes} rows={2} style={inputStyle} title={t('primary.notesTip')}
                        onChange={e => setPrimary(p => ({ ...p, notes: e.target.value }))} />
            </label>
          </div>
          <button type="submit" disabled={primary.saving} style={btnStyle('var(--c-primary)')} data-testid="primary-submit"
                  title={t('primary.submitTip')}>
            {primary.saving ? t('common:saving') : t('primary.submit')}
          </button>
        </form>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} style={formStyle} ref={setErrs.containerRef} data-testid="slant-set-form">
          <h3 ref={setFormHeadingRef} tabIndex={-1} style={{ marginTop: 0 }} data-testid="slant-set-title">{t('form.title')}</h3>
          <p style={requiredNoteStyle}>{t('common:validation.requiredNote')}</p>
          {formError && <p role="alert" style={formErrorStyle}>{formError}</p>}
          <div style={gridStyle}>
            {/* The message sits outside the <label>, which names the picker. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <label style={labelStyle}>
                {t('form.plateIdLabel')}
                <InvalidWithin required invalid={!!setErrs.errors.plate} errorId={errorIdFor('slant-set-plate')} data-field="plate">
                  <RecordAutocomplete
                    fetchUrl="/plates"
                    getLabel={r => r.plate_code}
                    getSecondary={r => r.protocol}
                    getValue={r => r.plate_id}
                    value={selectedPlate?.plate_id || ''}
                    onChange={val => { if (!val) setSelectedPlate(null); }}
                    onSelect={record => { setSelectedPlate(record); setErrs.clear('plate'); }}
                    placeholder={t('form.searchPlaceholder')}
                  />
                </InvalidWithin>
              </label>
              <FieldError id={errorIdFor('slant-set-plate')} testId="slant-set-plate-error">
                {validationMessage(t, setErrs.errors.plate)}
              </FieldError>
            </div>
            {selectedPlate && (
              <>
                <div style={{ ...labelStyle, gridColumn: 'span 2' }}>
                  {t('form.willCreate')}
                  <div style={{ fontSize: 13, color: 'var(--c-text-body-fg)', background: 'var(--c-bg-subtle)', border: '1px solid var(--c-border)', borderRadius: 6, padding: '8px 10px', fontWeight: 400 }}>
                    {[['A', 'active'], ['B', 'active'], ['C', 'refresh'], ['D', 'backup']].map(([suffix, role]) => (
                      <div key={suffix}>
                        {/* -S is main's slant-code scheme, and must match
                            utils.slant_codes_for, which now produces it. */}
                        <span style={{ fontFamily: 'monospace' }} dir="ltr">{selectedPlate.plate_code}-S{suffix}</span>
                        <span style={{ color: 'var(--c-text-muted-fg)', marginInlineStart: 8 }}>({t(`slantRole.${role}`)})</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{ ...labelStyle, gridColumn: 'span 2' }}>
                  {t('form.linkedRecord')}
                  <div style={{ fontSize: 13, color: 'var(--c-text-body-fg)', background: 'var(--c-bg-subtle)', border: '1px solid var(--c-border)', borderRadius: 6, padding: '6px 10px', fontWeight: 400 }}>
                    {selectedPlate.sample_id ? (
                      <span>{t('form.fieldCollection', { code: collectionsList.find(c => c.sample_id === selectedPlate.sample_id)?.qr_uid || selectedPlate.sample_id.slice(0, 8) + '…' })}</span>
                    ) : selectedPlate.external_id ? (
                      <span>{t('form.externalCollection', { code: externalList.find(e => e.external_id === selectedPlate.external_id)?.qr_uid || selectedPlate.external_id.slice(0, 8) + '…' })}</span>
                    ) : (
                      <span style={{ color: 'var(--c-text-faint-fg)' }}>{t('form.independentExperiment')}</span>
                    )}
                  </div>
                </div>
                {plateHasSlants && (
                  <div style={{ gridColumn: 'span 2', color: 'var(--c-danger-fg)', fontSize: 13 }}>
                    {t('form.alreadyExistsWarning')}
                  </div>
                )}
              </>
            )}
          </div>
          {/* Enabled while only the plate is missing -- pressing it says so,
              beside the picker. A plate that already has its set keeps it
              disabled: that has its own warning there. */}
          <button type="submit" disabled={submitting || plateHasSlants} style={{ ...btnStyle(plateHasSlants ? 'var(--c-border-strong)' : 'var(--c-primary)'), marginTop: 14 }} title={t('tip.createSet')}>
            {submitting ? t('form.creating') : t('form.createBtn')}
          </button>
        </form>
      )}

      {loading && (
        <OrganicLoader block motif="tube" label={t('common:loading')} slowLabel={t('common:stillWorking')} />
      )}
      {error && <p role="alert" style={formErrorStyle}>{error}</p>}

      {/* Nothing banked yet. Not shown while a create form is open: that is
          already the thing it would suggest. */}
      {!loading && !error && slants.length === 0 && !showForm && !primary.open && (
        <OrganicEmptyState kind="slants" title={t('noSlantsYet')} hint={t('empty.hint')}
          action={(
            <button type="button" onClick={toggleSlantForm} style={btnStyle('var(--c-primary)')}
                    data-testid="slants-empty-create" title={t('tip.createSlantSet')}>
              {t('empty.action')}
            </button>
          )} />
      )}

      {!loading && !error && slants.length > 0 && (
        <button type="button" onClick={() => setTableExpanded(e => !e)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-primary-fg)', fontSize: 14, fontWeight: 600, padding: '4px 0', marginBottom: 8 }} title={t('tip.toggleRecords')}>
          {tableExpanded ? t('table.hideRecords', { count: slants.length }) : t('table.showRecords', { count: slants.length })}
        </button>
      )}

      {!loading && !error && tableExpanded && slants.length > 0 && (
          <div className="table-wrap">
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
            <thead>
              <tr style={{ background: 'var(--c-bg-muted-2)' }}>
                {[t('table.isolateId'), t('table.labCode'), t('table.qr'), t('table.code'), t('table.isolateCode'), t('table.role'), t('table.medium'), t('table.status'), t('table.location'), t('table.gen'), t('table.current'), t('table.sampleExternal'), t('table.created'), t('table.lastCheck'), t('table.actions')].map(h => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {slants.map(s => (
                <tr key={s.slant_id} style={{ borderBottom: '1px solid var(--c-bg-muted-3)', cursor: 'pointer' }} onClick={() => setDetailSlant(s)} title={t('tip.openSlant')}>
                  {/* The Isolate ID: the specimen the slant descends from, not its line's code;
                      then that specimen's lab code. */}
                  <CodeCell code={s.specimen_code} testId="slant-specimen-code-cell" />
                  <CodeCell code={s.lab_code} testId="slant-lab-code-cell" />
                  <td style={tdStyle}><QRCodeLabel value={s.slant_code} size={40} compact /></td>
                  <td style={tdStyle} dir="ltr">{s.slant_code}</td>
                  <td style={tdStyle} dir="ltr" data-testid="slant-isolate-code">{s.isolate_code || '—'}</td>
                  <td style={tdStyle}>{badge(s.slant_role, ROLE_COLORS, 'slantRole', t)}</td>
                  <td style={tdStyle} dir="ltr">{s.protocol || '—'}</td>
                  <td style={tdStyle}>{badge(s.status, STATUS_COLORS, 'slantStatus', t)}</td>
                  <td style={{ ...tdStyle, whiteSpace: 'nowrap' }} dir="ltr" data-testid="slant-location"
                      title={(s.storage_unit && describeLocation(s, tSpec)) || undefined}>
                    {shortLocation(s, tSpec) || '—'}
                  </td>
                  <td style={tdStyle}>{s.generation}</td>
                  <td style={tdStyle}>{s.is_current_generation ? '✓' : '—'}</td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 12 }} dir="ltr">
                    {s.sample_id
                      ? s.sample_id.slice(0, 8) + '…'
                      : s.external_id
                        ? 'ext:' + s.external_id.slice(0, 8) + '…'
                        : '—'}
                  </td>
                  <td style={tdStyle}>{s.created_at ? new Date(s.created_at).toLocaleDateString() : '—'}</td>
                  <td style={tdStyle}>{s.last_check_date ? new Date(s.last_check_date).toLocaleDateString() : '—'}</td>
                  <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                    <button onClick={(e) => { e.stopPropagation(); openModal(s.slant_id, 'usage'); }} style={smallBtn('var(--c-info)')} title={t('tip.logUse')}>{t('actions.logUse')}</button>
                    {' '}
                    <button onClick={(e) => { e.stopPropagation(); openModal(s.slant_id, 'review'); }} style={smallBtn('var(--c-purple-2)')} title={t('tip.review')}>{t('actions.review')}</button>
                    {' '}
                    <button onClick={(e) => { e.stopPropagation(); setPhotoSlant(s); }} style={smallBtn('var(--c-text-muted)')} title={t('tip.photos')}>{t('actions.photos')}</button>
                    {' '}
                    {canDelete && (
                      <button onClick={(e) => { e.stopPropagation(); setDeleteError(''); setDeleteTarget(s.slant_id); }} style={smallBtn('var(--c-danger)')} title={t('tip.delete')}>{t('common:delete')}</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
      )}

      {photoSlant && (
        <div style={overlayStyle} onClick={() => setPhotoSlant(null)}>
          <div role="dialog" aria-modal="true" style={{ ...modalStyle, width: 620 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
              <div>
                <h3 style={{ margin: '0 0 2px' }} dir="ltr">{photoSlant.slant_code}</h3>
                <p style={{ margin: 0, fontSize: 13, color: 'var(--c-text-muted-fg)' }}>
                  {t(`slantRole.${photoSlant.slant_role}`)} · <span dir="ltr">{photoSlant.taxon_name || t('photoModal.unidentified')}</span>
                </p>
              </div>
              <button onClick={() => setPhotoSlant(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--c-text-muted-fg)', lineHeight: 1 }} title={t('tip.close')}>×</button>
            </div>
            <PhotoSection
              fetchUrl={`/slants/${photoSlant.slant_id}/photos`}
              uploadBody={{ slant_id: photoSlant.slant_id }}
              taxonName={photoSlant.taxon_name}
              sourceType="lab"
            />
          </div>
        </div>
      )}

      {detailSlant && (
        <div style={overlayStyle} onClick={() => setDetailSlant(null)}>
          <div role="dialog" aria-modal="true" style={{ ...modalStyle, width: 480 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <div>
                <h3 style={{ margin: '0 0 2px' }} dir="ltr">{detailSlant.slant_code}</h3>
                <p style={{ margin: 0, fontSize: 13, color: 'var(--c-text-muted-fg)' }}>
                  <span dir="ltr">{detailSlant.taxon_name || t('photoModal.unidentified')}</span>
                </p>
              </div>
              <button onClick={() => setDetailSlant(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--c-text-muted-fg)', lineHeight: 1 }} title={t('tip.close')}>×</button>
            </div>

            <div style={{ marginBottom: 12 }}>
              <QRCodeLabel value={detailSlant.slant_code} caption={detailSlant.isolate_code || ''} />
              <div style={{ fontSize: 13, marginTop: 6 }} data-testid="slant-detail-isolate">
                <span style={{ color: 'var(--c-text-muted-fg)' }}>{t('table.isolateCode')}: </span>
                <span dir="ltr">{detailSlant.isolate_code || '—'}</span>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 12px', fontSize: 13 }}>
              <div style={{ color: 'var(--c-text-muted-fg)' }}>{t('table.role')}</div>
              <div>{badge(detailSlant.slant_role, ROLE_COLORS, 'slantRole', t)}</div>

              <div style={{ color: 'var(--c-text-muted-fg)' }}>{t('table.medium')}</div>
              <div dir="ltr">{detailSlant.protocol || '—'}</div>

              <div style={{ color: 'var(--c-text-muted-fg)' }}>{t('table.status')}</div>
              <div>{badge(detailSlant.status, STATUS_COLORS, 'slantStatus', t)}</div>

              <div style={{ color: 'var(--c-text-muted-fg)' }}>{t('table.gen')}</div>
              <div>{detailSlant.generation}</div>

              <div style={{ color: 'var(--c-text-muted-fg)' }}>{t('table.current')}</div>
              <div>{detailSlant.is_current_generation ? '✓' : '—'}</div>

              <div style={{ color: 'var(--c-text-muted-fg)' }}>{t('detailModal.plateId')}</div>
              <div dir="ltr" style={{ fontFamily: 'monospace', fontSize: 12 }}>{detailSlant.plate_id || '—'}</div>

              <div style={{ color: 'var(--c-text-muted-fg)' }}>{t('table.sampleExternal')}</div>
              <div dir="ltr" style={{ fontFamily: 'monospace', fontSize: 12 }}>
                {detailSlant.sample_id
                  ? detailSlant.sample_id.slice(0, 8) + '…'
                  : detailSlant.external_id
                    ? 'ext:' + detailSlant.external_id.slice(0, 8) + '…'
                    : '—'}
              </div>

              <div style={{ color: 'var(--c-text-muted-fg)' }}>{t('table.created')}</div>
              <div>{detailSlant.created_at ? new Date(detailSlant.created_at).toLocaleDateString() : '—'}</div>

              <div style={{ color: 'var(--c-text-muted-fg)' }}>{t('table.lastCheck')}</div>
              <div>{detailSlant.last_check_date ? new Date(detailSlant.last_check_date).toLocaleDateString() : '—'}</div>
            </div>

            <SlantStoragePanel key={detailSlant.slant_id} slant={detailSlant} canEdit={canStore} users={userList} currentUser={user}
                               onSaved={storageSaved} />

            {(detailSlant.dna_records || []).length > 0 && (
              <div style={{ marginTop: 16, borderTop: '1px solid var(--c-border)', paddingTop: 12 }}>
                <strong style={{ fontSize: 13 }}>{t('detailModal.dnaTitle')}</strong>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
                  {detailSlant.dna_records.map(d => (
                    <div key={d.dna_id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, background: 'var(--c-bg-muted)', borderRadius: 6, padding: '4px 10px', fontSize: 12 }}>
                      <span dir="ltr">{d.isolate_code}</span>
                      <span style={{ color: 'var(--c-text-muted-fg)' }}>{t(`detailModal.dnaRelation.${d.relation}`)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {canAssign && (
              <div style={{ marginTop: 16, borderTop: '1px solid var(--c-border)', paddingTop: 12 }}>
                <strong style={{ fontSize: 13 }}>{t('detailModal.assignedToTitle')}</strong>
                {assignmentsLoading ? (
                  <p style={{ color: 'var(--c-text-muted-fg)', fontSize: 12 }}>
                    <OrganicLoader inline showLabel announce={false} label={t('common:loading')} slowLabel={t('common:stillWorking')} />
                  </p>
                ) : (
                  <div style={{ marginTop: 6 }}>
                    {slantAssignments.length === 0 ? (
                      <p style={{ color: 'var(--c-text-muted-fg)', fontSize: 12 }}>{t('detailModal.noAssignments')}</p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
                        {slantAssignments.map(a => (
                          <div key={a.assignment_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--c-bg-muted)', borderRadius: 6, padding: '4px 10px', fontSize: 12 }}>
                            <span>{a.username}</span>
                            <button onClick={() => removeSlantAssignment(a.assignment_id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text-faint-fg)', fontSize: 13 }} title={t('tip.removeAssignment')}>×</button>
                          </div>
                        ))}
                      </div>
                    )}
                    {assignError && <p role="alert" style={{ ...formErrorStyle, fontSize: 12, margin: 0 }}>{assignError}</p>}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                      <select value={assignUserId} onChange={e => setAssignUserId(e.target.value)} style={{ ...inputStyle, flex: 1, fontSize: 12 }} title={t('tip.assignUser')}>
                        <option value="">{t('detailModal.assignSelectPlaceholder')}</option>
                        {allUsers.map(u => (
                          <option key={u.user_id} value={u.user_id}>{u.username}</option>
                        ))}
                      </select>
                      <button onClick={submitSlantAssignment} disabled={assignSaving || !assignUserId} style={smallBtn('var(--c-primary)')} title={t('tip.assign')}>
                        {assignSaving ? t('common:saving') : t('detailModal.assignBtn')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={() => setDetailSlant(null)} style={smallBtn('var(--c-text-muted)')} title={t('tip.close')}>{t('common:close')}</button>
            </div>
          </div>
        </div>
      )}

      {activeModal && (
        <div style={overlayStyle} onClick={closeModal}>
          <div role="dialog" aria-modal="true" style={modalStyle} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>
              {activeModal.type === 'usage' ? t('usageModal.title') : t('reviewModal.title')}
            </h3>
            {modalError && <p role="alert" style={formErrorStyle}>{modalError}</p>}
            <form onSubmit={submitModal}>
              {activeModal.type === 'usage' ? (
                <>
                  <label style={{ ...labelStyle, marginBottom: 12 }}>
                    {t('usageModal.usedByLabel')}
                    <select name="used_by" value={modalForm.used_by || ''} onChange={handleModalChange} style={inputStyle} title={t('tip.usedBy')}>
                      <option value="">{t('selectUserPlaceholder')}</option>
                      {userList.map(u => <option key={u.user_id} value={u.user_id}>{u.username}</option>)}
                    </select>
                  </label>
                  <label style={{ ...labelStyle, marginBottom: 12 }}>
                    {t('usageModal.purposeLabel')}
                    <select name="purpose" value={modalForm.purpose || ''} onChange={handleModalChange} style={inputStyle} title={t('tip.purpose')}>
                      {PURPOSES.map(p => <option key={p} value={p}>{t(`purpose.${p}`)}</option>)}
                    </select>
                  </label>
                  <label style={{ ...labelStyle, marginBottom: 16 }}>
                    {t('usageModal.notesLabel')}
                    <textarea name="notes" value={modalForm.notes || ''} onChange={handleModalChange} rows={3} style={inputStyle} title={t('tip.usageNotes')} />
                  </label>
                </>
              ) : (
                <>
                  <label style={{ ...labelStyle, marginBottom: 12 }}>
                    {t('reviewModal.reviewedByLabel')}
                    <select name="reviewed_by" value={modalForm.reviewed_by || ''} onChange={handleModalChange} style={inputStyle} title={t('tip.reviewedBy')}>
                      <option value="">{t('selectUserPlaceholder')}</option>
                      {userList.map(u => <option key={u.user_id} value={u.user_id}>{u.username}</option>)}
                    </select>
                  </label>
                  <label style={{ ...labelStyle, marginBottom: 12 }}>
                    {t('reviewModal.statusAtReviewLabel')}
                    <select name="status_at_review" value={modalForm.status_at_review || ''} onChange={handleModalChange} style={inputStyle} title={t('tip.statusAtReview')}>
                      {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{t(`slantStatus.${s}`)}</option>)}
                    </select>
                  </label>
                  <label style={{ ...labelStyle, marginBottom: 12 }}>
                    {t('reviewModal.actionTakenLabel')}
                    <input name="action_taken" value={modalForm.action_taken || ''} onChange={handleModalChange} style={inputStyle} title={t('tip.actionTaken')} />
                  </label>
                  <label style={{ ...labelStyle, marginBottom: 16 }}>
                    {t('reviewModal.notesLabel')}
                    <textarea name="notes" value={modalForm.notes || ''} onChange={handleModalChange} rows={3} style={inputStyle} title={t('tip.reviewNotes')} />
                  </label>
                </>
              )}
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="submit" disabled={modalSaving} style={btnStyle('var(--c-primary)')} title={t('tip.save')}>
                  {modalSaving ? t('common:saving') : t('common:save')}
                </button>
                <button type="button" onClick={closeModal} style={btnStyle('var(--c-text-muted)')} title={t('tip.cancel')}>{t('common:cancel')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDeleteModal
        open={!!deleteTarget}
        title={t('actions.deleteConfirmTitle')}
        consequences={[t('actions.deleteConsequence1'), t('actions.deleteConsequence2')]}
        deleting={deletingSlant}
        error={deleteError}
        onConfirm={confirmDeleteSlant}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

const thStyle   = { padding: '8px 12px', textAlign: 'start', fontWeight: 600 };
const tdStyle   = { padding: '8px 12px' };
const codeStyle = { fontFamily: 'monospace', fontSize: 12 };

// An isolate's code in a table cell: monospace, left to right in Hebrew too,
// and '—' when there is none.
function CodeCell({ code, testId }) {
  return (
    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }} data-testid={testId}>
      {code ? <bdi dir="ltr" style={codeStyle}>{code}</bdi> : '—'}
    </td>
  );
}

// A refusal said once, above the form's fields: in the theme's danger ink,
// and announced, since focus stays on the button that was pressed.
const formErrorStyle = { color: 'var(--c-danger-fg)', margin: '0 0 12px' };
const requiredNoteStyle = { margin: '0 0 12px', fontSize: 12, color: 'var(--c-text-muted-fg)' };

const formStyle = {
  background: 'var(--c-bg-subtle)',
  border: '1px solid var(--c-border)',
  borderRadius: 8,
  padding: 20,
  marginBottom: 24,
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
  gap: 14,
  marginBottom: 16,
};

const labelStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 14,
  fontWeight: 500,
};

const inputStyle = {
  padding: '6px 10px',
  border: '1px solid var(--c-border-strong)',
  borderRadius: 6,
  fontSize: 14,
  fontWeight: 400,
};

const btnStyle = (bg) => ({
  background: bg,
  color: 'var(--c-on-accent)',
  border: 'none',
  borderRadius: 6,
  padding: '8px 16px',
  cursor: 'pointer',
  fontSize: 14,
});

const smallBtn = (bg) => ({
  background: bg,
  color: 'var(--c-on-accent)',
  border: 'none',
  borderRadius: 4,
  padding: '3px 10px',
  cursor: 'pointer',
  fontSize: 12,
});

// A dialog taller than the window used to be centred by the overlay, which
// pushed its top (title, close button, first field) above the screen where
// nothing could scroll to it. The overlay scrolls now, and margin:auto on
// the dialog centres it only while it fits.
const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  display: 'flex',
  alignItems: 'flex-start',
  overflowY: 'auto',
  padding: '24px 16px',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalStyle = {
  background: 'var(--c-surface)',
  margin: 'auto',
  borderRadius: 10,
  padding: 28,
  width: 420,
  maxWidth: '90vw',
  boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
};
